import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Minus, Zap,
  Target, Eye, Radio, Clock, MapPin, Trophy, BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { apiGet } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };
const divider = { borderBottom: '1px solid #27272A' };
const rowBorder = { borderBottom: '1px solid #1F1F22' };

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">{t.common.tennis}</span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">{t.common.table_tennis}</span>
  );
}

function StatusBadge({ status }) {
  if (status === 'live')
    return (<span className="flex items-center gap-1.5 text-sm text-red-400 font-medium"><span className="w-2 h-2 rounded-full bg-red-500 live-dot" />{t.status.live}</span>);
  if (status === 'scheduled')
    return (<span className="px-2 py-0.5 text-sm rounded bg-amber-500/15 text-amber-400">{t.status.scheduled}</span>);
  return (<span className="px-2 py-0.5 text-sm rounded bg-zinc-700/40 text-zinc-500">{t.status.finished}</span>);
}

function SignalStatusBadge({ status }) {
  const m = { active: 'bg-emerald-500/15 text-emerald-400', won: 'bg-blue-500/15 text-blue-400', lost: 'bg-red-500/15 text-red-400' };
  const l = { active: t.status.active, won: t.status.won, lost: t.status.lost };
  return <span className={`px-1.5 py-0.5 text-xs rounded ${m[status] || 'bg-zinc-500/15 text-zinc-400'}`}>{l[status] || status}</span>;
}

function StatRow({ label, val1, val2, highlight }) {
  return (
    <div className="flex items-center gap-3 py-2.5" style={rowBorder}>
      <div className={`text-right flex-1 text-sm font-medium ${highlight === 'p1' ? 'text-emerald-400' : 'text-zinc-300'}`} style={mono}>{val1}</div>
      <div className="text-xs text-zinc-500 w-24 text-center">{label}</div>
      <div className={`flex-1 text-sm font-medium ${highlight === 'p2' ? 'text-emerald-400' : 'text-zinc-300'}`} style={mono}>{val2}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, iconColor, bgColor, label, mainValue, mainColor, sub1, sub2 }) {
  return (
    <div className="rounded-sm p-4" style={card} data-testid={`summary-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-6 h-6 rounded-sm flex items-center justify-center ${bgColor}`}><Icon size={12} className={iconColor} /></div>
        <span className="text-xs font-medium uppercase tracking-[0.08em] text-zinc-500">{label}</span>
      </div>
      <div className={`text-xl font-bold mb-0.5 ${mainColor}`} style={mono}>{mainValue}</div>
      {sub1 && <div className="text-xs text-zinc-400">{sub1}</div>}
      {sub2 && <div className="text-xs text-zinc-600 mt-0.5">{sub2}</div>}
    </div>
  );
}

function OddsChart({ snapshots }) {
  const chartData = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    const grouped = {};
    snapshots.forEach((os) => {
      const key = os.captured_at;
      if (!grouped[key]) grouped[key] = { time: new Date(os.captured_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) };
      const label = `${os.selection_name} (${os.bookmaker_name})`;
      grouped[key][label] = os.odds;
    });
    return Object.values(grouped);
  }, [snapshots]);

  const lineKeys = useMemo(() => {
    const keys = new Set();
    chartData.forEach((d) => Object.keys(d).filter((k) => k !== 'time').forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [chartData]);

  const colors = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#A855F7', '#06B6D4', '#F97316', '#EC4899'];

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-sm p-5" style={card} data-testid="odds-chart-section">
      <h2 className="font-semibold text-zinc-200 text-sm mb-4" style={heading}>{t.match_detail.historical_odds}</h2>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <XAxis dataKey="time" tick={{ fill: '#52525B', fontSize: 10 }} axisLine={{ stroke: '#27272A' }} tickLine={false} />
          <YAxis tick={{ fill: '#52525B', fontSize: 10 }} axisLine={{ stroke: '#27272A' }} tickLine={false} domain={['auto', 'auto']} />
          <Tooltip
            contentStyle={{ background: '#0D0D0F', border: '1px solid #27272A', borderRadius: 2, fontSize: 11, color: '#D4D4D8' }}
            labelStyle={{ color: '#71717A' }}
          />
          <Legend wrapperStyle={{ fontSize: 10, color: '#A1A1AA' }} />
          {lineKeys.map((key, i) => (
            <Line key={key} type="monotone" dataKey={key} stroke={colors[i % colors.length]} dot={{ r: 3 }} strokeWidth={2} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MatchDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet(`/matches/${id}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center gap-2 justify-center py-16 text-zinc-500"><Loader2 size={18} className="animate-spin" /><span>{t.common.loading}</span></div>;
  if (error) return <div className="text-red-400 text-center py-10">{t.common.error}: {error}</div>;
  if (!data) return null;

  const { match, prematch_signals, live_signals, odds_snapshots, score_snapshots, player1, player2, summary, deep_analysis } = data;
  const prematchOdds = odds_snapshots?.filter((o) => !o.is_live) || [];
  const liveOdds = odds_snapshots?.filter((o) => o.is_live) || [];
  const allSignals = [
    ...(prematch_signals || []).map((s) => ({ ...s, _type: 'prematch' })),
    ...(live_signals || []).map((s) => ({ ...s, _type: 'live' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="space-y-5 fade-in">
      {/* Back */}
      <Link to="/incontri" data-testid="back-to-matches" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
        <ArrowLeft size={15} /> {t.common.back}
      </Link>

      {/* ====== 1. MATCH HEADER ====== */}
      <div className="rounded-sm p-6" style={card} data-testid="match-header">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <SportBadge sport={match.sport} />
            <StatusBadge status={match.status} />
            {match.surface && <span className="text-xs text-zinc-600 px-1.5 py-0.5 rounded bg-zinc-800/60"><MapPin size={10} className="inline mr-1" />{match.surface}</span>}
            {match.format && <span className="text-xs text-zinc-700 px-1.5 py-0.5 rounded bg-zinc-800/40">{match.format.replace('_', ' ')}</span>}
          </div>
          <div className="text-xs text-zinc-500 text-right">
            <div className="font-medium">{match.tournament_name}</div>
            {match.round && <div className="text-zinc-600">{match.round}</div>}
            <div className="text-zinc-700 mt-0.5 flex items-center gap-1 justify-end"><Clock size={10} />{new Date(match.start_time).toLocaleString('it-IT', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-6 py-5">
          <div className="text-center flex-1">
            <Link to={`/giocatori/${match.player1_id}`} className="hover:text-emerald-400 transition-colors">
              <div className={`text-2xl font-bold ${match.winner_id === match.player1_id ? 'text-emerald-400' : 'text-zinc-100'}`} style={heading}>{match.player1_name}</div>
            </Link>
            {player1 && <div className="text-xs text-zinc-500 mt-1">{player1.country} | Rating {player1.current_rating.toFixed(0)} | Forma {player1.recent_form_score.toFixed(0)}</div>}
          </div>
          <div className="text-center px-4">
            {score_snapshots?.length > 0 ? (
              <div className="space-y-0.5">
                {score_snapshots.map((ss) => (
                  <div key={ss.id} className="flex items-center gap-2 justify-center">
                    <span className="text-xs text-zinc-600" style={mono}>S{ss.set_number}</span>
                    <span className="text-xl font-bold text-zinc-200" style={mono}>{ss.player1_games_or_points}-{ss.player2_games_or_points}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-zinc-600 text-lg" style={mono}>{match.status === 'scheduled' ? new Date(match.start_time).toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' }) : '—'}</div>
            )}
            <div className="text-zinc-700 text-xs mt-2">vs</div>
          </div>
          <div className="text-center flex-1">
            <Link to={`/giocatori/${match.player2_id}`} className="hover:text-emerald-400 transition-colors">
              <div className={`text-2xl font-bold ${match.winner_id === match.player2_id ? 'text-emerald-400' : 'text-zinc-100'}`} style={heading}>{match.player2_name}</div>
            </Link>
            {player2 && <div className="text-xs text-zinc-500 mt-1">{player2.country} | Rating {player2.current_rating.toFixed(0)} | Forma {player2.recent_form_score.toFixed(0)}</div>}
          </div>
        </div>
        {match.winner_name && <div className="text-center mt-2 text-sm text-emerald-400"><Trophy size={13} className="inline mr-1" />Vincitore: <span className="font-semibold">{match.winner_name}</span></div>}
      </div>

      {/* ====== 6. QUICK SUMMARY ====== */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="quick-summary">
        <SummaryCard
          icon={Zap} iconColor="text-emerald-400" bgColor="bg-emerald-500/10"
          label={t.match_detail.best_prematch_angle}
          mainValue={summary?.best_prematch_angle ? `+${(summary.best_prematch_angle.expected_value * 100).toFixed(1)}% EV` : '—'}
          mainColor="text-emerald-400"
          sub1={summary?.best_prematch_angle?.selection_name}
          sub2={summary?.best_prematch_angle ? `@ ${summary.best_prematch_angle.bookmaker_odds?.toFixed(2)} (${summary.best_prematch_angle.bookmaker_name}) | Conf: ${(summary.best_prematch_angle.confidence_score * 100).toFixed(0)}%` : null}
        />
        <SummaryCard
          icon={Radio} iconColor="text-red-400" bgColor="bg-red-500/10"
          label={t.match_detail.best_live_angle}
          mainValue={summary?.best_live_angle ? `+${(summary.best_live_angle.expected_value * 100).toFixed(1)}% EV` : '—'}
          mainColor="text-red-400"
          sub1={summary?.best_live_angle?.selection_name}
          sub2={summary?.best_live_angle ? `@ ${summary.best_live_angle.live_odds?.toFixed(2)} (${summary.best_live_angle.bookmaker_name}) | Mom: ${summary.best_live_angle.momentum_score?.toFixed(0)}` : null}
        />
        <SummaryCard
          icon={Eye} iconColor={summary?.watch_live_flag ? 'text-amber-400' : 'text-zinc-600'} bgColor={summary?.watch_live_flag ? 'bg-amber-500/10' : 'bg-zinc-800'}
          label={t.match_detail.follow_live}
          mainValue={summary?.watch_live_flag ? 'Si' : 'No'}
          mainColor={summary?.watch_live_flag ? 'text-amber-400' : 'text-zinc-600'}
          sub1={summary?.watch_live_flag ? t.match_detail.high_volatility : t.match_detail.no_prematch_flag}
        />
      </div>

      {/* ====== DEEP ANALYSIS (H2H + Form) ====== */}
      {deep_analysis && (deep_analysis.h2h || deep_analysis.player1_form || deep_analysis.player2_form) && (
        <div className="rounded-sm" style={card} data-testid="deep-analysis-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <BarChart3 size={15} className="text-cyan-400" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.match_detail.deep_analysis || 'Analisi Approfondita'}</h2>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-400 font-medium ml-auto">scores24.live</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-x" style={{ borderColor: '#27272A' }}>
            {/* H2H Section */}
            {deep_analysis.h2h && deep_analysis.h2h.total_matches > 0 && (
              <div className="p-5">
                <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{t.match_detail.head_to_head || 'Precedenti (H2H)'}</h3>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${deep_analysis.h2h.p1_pct > deep_analysis.h2h.p2_pct ? 'text-emerald-400' : 'text-zinc-400'}`} style={mono}>{deep_analysis.h2h.p1_pct}%</div>
                    <div className="text-xs text-zinc-500">{deep_analysis.h2h.p1_wins}W</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-zinc-600 mb-0.5">{deep_analysis.h2h.total_matches} match</div>
                    <div className="w-32 h-2 rounded-full bg-zinc-800 overflow-hidden flex">
                      <div className="h-full bg-emerald-500" style={{ width: `${deep_analysis.h2h.p1_pct}%` }} />
                      <div className="h-full bg-cyan-500" style={{ width: `${deep_analysis.h2h.p2_pct}%` }} />
                    </div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${deep_analysis.h2h.p2_pct > deep_analysis.h2h.p1_pct ? 'text-cyan-400' : 'text-zinc-400'}`} style={mono}>{deep_analysis.h2h.p2_pct}%</div>
                    <div className="text-xs text-zinc-500">{deep_analysis.h2h.p2_wins}W</div>
                  </div>
                </div>
                {deep_analysis.h2h.recent_h2h?.slice(0, 3).map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5" style={{ borderBottom: '1px solid #1C1C1E' }}>
                    <span className="text-zinc-500">{m.date}</span>
                    <span className="text-zinc-400">{m.player1_name?.split(' ').pop()} vs {m.player2_name?.split(' ').pop()}</span>
                    <span className={`font-mono font-semibold ${m.result === 'W' ? 'text-emerald-400' : 'text-red-400'}`}>{m.score} {m.result}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Player 1 Form */}
            {deep_analysis.player1_form && (
              <div className="p-5">
                <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{match.player1_name?.split(' ').pop()} — {t.match_detail.recent_form || 'Forma'}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-zinc-500">{deep_analysis.player1_form.wins}W-{deep_analysis.player1_form.losses}L</span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${deep_analysis.player1_form.form_score >= 60 ? 'bg-emerald-500/15 text-emerald-400' : deep_analysis.player1_form.form_score >= 40 ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>
                    {deep_analysis.player1_form.form_score}/100
                  </span>
                  {deep_analysis.player1_form.streak && <span className="text-xs text-zinc-500">Serie: {deep_analysis.player1_form.streak}</span>}
                </div>
                {deep_analysis.player1_form.recent_results?.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1" style={{ borderBottom: '1px solid #1C1C1E' }}>
                    <span className={`font-semibold w-4 ${m.result === 'W' ? 'text-emerald-400' : 'text-red-400'}`}>{m.result}</span>
                    <span className="text-zinc-400 flex-1 truncate mx-2">vs {m.player2_name?.split(' ').pop()}</span>
                    <span className="text-zinc-600 font-mono">{m.score}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Player 2 Form */}
            {deep_analysis.player2_form && (
              <div className="p-5">
                <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{match.player2_name?.split(' ').pop()} — {t.match_detail.recent_form || 'Forma'}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-zinc-500">{deep_analysis.player2_form.wins}W-{deep_analysis.player2_form.losses}L</span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${deep_analysis.player2_form.form_score >= 60 ? 'bg-emerald-500/15 text-emerald-400' : deep_analysis.player2_form.form_score >= 40 ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400'}`}>
                    {deep_analysis.player2_form.form_score}/100
                  </span>
                  {deep_analysis.player2_form.streak && <span className="text-xs text-zinc-500">Serie: {deep_analysis.player2_form.streak}</span>}
                </div>
                {deep_analysis.player2_form.recent_results?.slice(0, 5).map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1" style={{ borderBottom: '1px solid #1C1C1E' }}>
                    <span className={`font-semibold w-4 ${m.result === 'W' ? 'text-emerald-400' : 'text-red-400'}`}>{m.result}</span>
                    <span className="text-zinc-400 flex-1 truncate mx-2">vs {m.player2_name?.split(' ').pop()}</span>
                    <span className="text-zinc-600 font-mono">{m.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== 2. PREMATCH ANALYSIS ====== */}
      <div className="rounded-sm" style={card} data-testid="prematch-analysis-section">
        <div className="px-5 py-4 flex items-center gap-2" style={divider}>
          <TrendingUp size={15} className="text-emerald-400" />
          <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.match_detail.prematch_analysis}</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-x" style={{ borderColor: '#27272A' }}>
          {/* Player comparison */}
          <div className="p-5">
            <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{t.match_detail.player_comparison}</h3>
            {player1 && player2 ? (
              <>
                <div className="flex justify-between text-xs text-zinc-400 mb-2">
                  <span className="font-semibold">{player1.name}</span>
                  <span className="font-semibold">{player2.name}</span>
                </div>
                <StatRow label={t.players.rating} val1={player1.current_rating.toFixed(0)} val2={player2.current_rating.toFixed(0)} highlight={player1.current_rating > player2.current_rating ? 'p1' : 'p2'} />
                <StatRow label={t.players.form_score} val1={player1.recent_form_score.toFixed(0)} val2={player2.recent_form_score.toFixed(0)} highlight={player1.recent_form_score > player2.recent_form_score ? 'p1' : 'p2'} />
                {match.sport === 'tennis' && (
                  <>
                    <StatRow label={t.players.surface_hard} val1={player1.surface_rating_hard?.toFixed(0) || '—'} val2={player2.surface_rating_hard?.toFixed(0) || '—'} highlight={(player1.surface_rating_hard || 0) > (player2.surface_rating_hard || 0) ? 'p1' : 'p2'} />
                    <StatRow label={t.players.surface_clay} val1={player1.surface_rating_clay?.toFixed(0) || '—'} val2={player2.surface_rating_clay?.toFixed(0) || '—'} highlight={(player1.surface_rating_clay || 0) > (player2.surface_rating_clay || 0) ? 'p1' : 'p2'} />
                    <StatRow label={t.players.surface_grass} val1={player1.surface_rating_grass?.toFixed(0) || '—'} val2={player2.surface_rating_grass?.toFixed(0) || '—'} highlight={(player1.surface_rating_grass || 0) > (player2.surface_rating_grass || 0) ? 'p1' : 'p2'} />
                  </>
                )}
                <StatRow label={t.players.country} val1={player1.country} val2={player2.country} highlight={null} />
              </>
            ) : <div className="text-zinc-600 text-sm">{t.match_detail.player_data_unavailable}</div>}
          </div>
          {/* Prematch odds + signals */}
          <div className="p-5">
            <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{t.match_detail.prematch_odds_signals}</h3>
            {prematchOdds.length > 0 && (
              <div className="mb-4">
                <table className="w-full text-sm">
                  <thead><tr style={rowBorder}>
                    <th className="text-left text-xs text-zinc-600 pb-2">{t.match_detail.th_selection}</th>
                    <th className="text-left text-xs text-zinc-600 pb-2">{t.match_detail.th_bookmaker}</th>
                    <th className="text-right text-xs text-zinc-600 pb-2">{t.match_detail.th_odds}</th>
                    <th className="text-right text-xs text-zinc-600 pb-2">{t.match_detail.th_implied_prob}</th>
                  </tr></thead>
                  <tbody>
                    {prematchOdds.map((os) => (
                      <tr key={os.id} style={rowBorder}>
                        <td className="py-2 text-zinc-300 text-xs">{os.selection_name}</td>
                        <td className="py-2 text-zinc-500 text-xs flex items-center gap-1.5">
                          {os.bookmaker_name}
                          {os.odds_rank === 'best' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">{t.admin.quota_best}</span>}
                          {os.odds_rank === 'worst' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-600/30 text-zinc-500 font-medium">{t.admin.quota_worst}</span>}
                        </td>
                        <td className={`py-2 text-right ${os.odds_rank === 'best' ? 'text-emerald-400' : 'text-zinc-500'}`} style={{ ...mono, fontSize: 12 }}>{os.odds.toFixed(2)}</td>
                        <td className="py-2 text-right text-zinc-400" style={{ ...mono, fontSize: 11 }}>{(os.implied_probability * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {prematch_signals?.length > 0 ? prematch_signals.map((sig) => (
              <div key={sig.id} data-testid={`pm-signal-${sig.id}`} className="py-2.5" style={rowBorder}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="text-sm font-medium text-zinc-200">{sig.selection_name}</span>
                    <span className="text-xs text-zinc-600 ml-2">{sig.market_type} | {sig.bookmaker_name}</span>
                    {sig.watch_live_flag && <span className="ml-2 text-xs text-amber-400"><Eye size={10} className="inline" /> Live</span>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-emerald-400 text-sm font-medium" style={mono}>{sig.bookmaker_odds?.toFixed(2)}</span>
                    <span className="text-xs text-zinc-500 ml-2" style={mono}>Fair {sig.fair_odds?.toFixed(2)}</span>
                    <div className="text-xs mt-0.5">
                      <span className="text-emerald-400" style={mono}>EV +{(sig.expected_value * 100).toFixed(1)}%</span>
                      <span className="text-zinc-500 ml-2" style={mono}>Conf {(sig.confidence_score * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : <div className="text-zinc-600 text-xs py-4">{t.match_detail.no_prematch_signal}</div>}
          </div>
        </div>
      </div>

      {/* ====== 3. LIVE ANALYSIS ====== */}
      {(match.status === 'live' || live_signals?.length > 0 || score_snapshots?.length > 0) && (
        <div className="rounded-sm" style={card} data-testid="live-analysis-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <span className="w-2 h-2 rounded-full bg-red-500 live-dot" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.match_detail.live_analysis}</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-x" style={{ borderColor: '#27272A' }}>
            {/* Score + Live odds */}
            <div className="p-5">
              <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{t.match_detail.current_score_live_odds}</h3>
              {score_snapshots?.length > 0 && (
                <div className="mb-4">
                  <table className="w-full text-sm">
                    <thead><tr style={rowBorder}>
                      <th className="text-left text-xs text-zinc-600 pb-2">Set</th>
                      <th className="text-center text-xs text-zinc-600 pb-2">{match.player1_name.split(' ').pop()}</th>
                      <th className="text-center text-xs text-zinc-600 pb-2">{match.player2_name.split(' ').pop()}</th>
                      <th className="text-right text-xs text-zinc-600 pb-2">Set Score</th>
                    </tr></thead>
                    <tbody>
                      {score_snapshots.map((ss) => (
                        <tr key={ss.id} style={rowBorder}>
                          <td className="py-2 text-zinc-400 text-xs">Set {ss.set_number}</td>
                          <td className="py-2 text-center text-zinc-200 font-medium" style={{ ...mono, fontSize: 13 }}>{ss.player1_games_or_points}</td>
                          <td className="py-2 text-center text-zinc-200 font-medium" style={{ ...mono, fontSize: 13 }}>{ss.player2_games_or_points}</td>
                          <td className="py-2 text-right text-zinc-500" style={{ ...mono, fontSize: 11 }}>{ss.player1_sets}-{ss.player2_sets}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {liveOdds.length > 0 && (
                <div>
                  <h4 className="text-xs text-zinc-600 mb-2">{t.match_detail.live_odds}</h4>
                  <table className="w-full text-sm">
                    <thead><tr style={rowBorder}>
                      <th className="text-left text-xs text-zinc-600 pb-2">{t.match_detail.th_selection}</th>
                      <th className="text-left text-xs text-zinc-600 pb-2">{t.match_detail.th_bookmaker}</th>
                      <th className="text-right text-xs text-zinc-600 pb-2">{t.match_detail.th_odds}</th>
                    </tr></thead>
                    <tbody>
                      {liveOdds.map((os) => (
                        <tr key={os.id} style={rowBorder}>
                          <td className="py-2 text-zinc-300 text-xs">{os.selection_name}</td>
                          <td className="py-2 text-zinc-500 text-xs">{os.bookmaker_name}</td>
                          <td className="py-2 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>{os.odds.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {/* Live signals with momentum/pressure */}
            <div className="p-5">
              <h3 className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3">{t.match_detail.live_signals_active}</h3>
              {live_signals?.length > 0 ? live_signals.map((sig) => (
                <div key={sig.id} data-testid={`live-signal-${sig.id}`} className="py-3" style={rowBorder}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-zinc-200">{sig.selection_name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{sig.market_type} | {sig.bookmaker_name}</div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-xs text-zinc-600">{t.signals.momentum}:</span>
                        {sig.momentum_score > 20 ? (
                          <span className="text-xs text-emerald-400 flex items-center gap-0.5"><TrendingUp size={11} /> +{sig.momentum_score.toFixed(0)}</span>
                        ) : sig.momentum_score < -20 ? (
                          <span className="text-xs text-red-400 flex items-center gap-0.5"><TrendingDown size={11} /> {sig.momentum_score.toFixed(0)}</span>
                        ) : (
                          <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Minus size={11} /> {sig.momentum_score.toFixed(0)}</span>
                        )}
                        <span className="text-xs text-zinc-600">{t.signals.pressure}:</span>
                        <span className={`text-xs ${sig.pressure_score >= 70 ? 'text-red-400' : sig.pressure_score >= 50 ? 'text-amber-400' : 'text-zinc-500'}`} style={mono}>{sig.pressure_score.toFixed(0)}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-emerald-400 text-sm font-medium" style={mono}>{sig.live_odds?.toFixed(2)}</div>
                      <div className="text-xs text-zinc-500" style={mono}>Fair {sig.fair_live_odds?.toFixed(2)}</div>
                      <div className="text-xs text-emerald-400 mt-0.5" style={mono}>EV +{(sig.expected_value * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              )) : <div className="text-zinc-600 text-xs py-4">{t.match_detail.no_live_signal}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ====== 4. ODDS HISTORY CHART ====== */}
      <OddsChart snapshots={odds_snapshots} />

      {/* ====== 5. SIGNAL HISTORY ====== */}
      {allSignals.length > 0 && (
        <div className="rounded-sm" style={card} data-testid="signal-history-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <Target size={15} className="text-zinc-400" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.match_detail.signal_history}</h2>
            <span className="ml-auto text-xs text-zinc-600">{allSignals.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={divider}>
                  {[t.match_detail.th_type, t.match_detail.th_market, t.match_detail.th_selection, t.match_detail.th_bookmaker, t.match_detail.th_odds, t.match_detail.th_fair_odds, t.match_detail.th_ev_pct, t.match_detail.th_conf_mom, t.match_detail.th_status, t.match_detail.th_time].map((h, i) => (
                    <th key={i} className={`py-2.5 px-4 text-xs uppercase tracking-[0.06em] text-zinc-600 font-medium whitespace-nowrap ${i >= 4 && i <= 7 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allSignals.map((sig) => {
                  const isPm = sig._type === 'prematch';
                  return (
                    <tr key={sig.id} data-testid={`history-signal-${sig.id}`} style={rowBorder} className="transition-colors hover:bg-zinc-800/30">
                      <td className="px-4 py-2.5">{isPm ? <span className="px-1.5 py-0.5 text-xs rounded bg-emerald-500/10 text-emerald-400">Prematch</span> : <span className="px-1.5 py-0.5 text-xs rounded bg-red-500/10 text-red-400">Live</span>}</td>
                      <td className="px-4 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{sig.market_type}</td>
                      <td className="px-4 py-2.5 text-zinc-300 font-medium text-xs">{sig.selection_name}</td>
                      <td className="px-4 py-2.5 text-zinc-500 text-xs">{sig.bookmaker_name || sig.bookmaker}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>{(isPm ? sig.bookmaker_odds : sig.live_odds)?.toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right text-zinc-500" style={{ ...mono, fontSize: 11 }}>{(isPm ? sig.fair_odds : sig.fair_live_odds)?.toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>+{(sig.expected_value * 100).toFixed(1)}%</td>
                      <td className="px-4 py-2.5 text-right text-zinc-400" style={{ ...mono, fontSize: 11 }}>{isPm ? `${(sig.confidence_score * 100).toFixed(0)}% ${t.match_detail.conf_label}` : `${sig.momentum_score?.toFixed(0)} ${t.match_detail.mom_label}`}</td>
                      <td className="px-4 py-2.5"><SignalStatusBadge status={sig.signal_status} /></td>
                      <td className="px-4 py-2.5 text-zinc-600" style={{ ...mono, fontSize: 10 }}>{new Date(sig.created_at).toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
